import styles from '@hts/entities/Card/styles/InfoContent/CardInfoContent.module.scss';
import { HtsTypography } from '@hts/shared/ui';
import { isEmptyObject } from '@hts/shared/utils';
import { memo, useCallback, useMemo, useState } from 'react';
import { useCardContext } from '../../libs';
import { AddMusic } from './AddMusic';
import { BuyButtons } from './Buy';
import { ResendButton } from './Resend';
import { TextFields } from './TextFields';
import { TopButtons } from './TopButtons';

interface ICardInfoContentProps {
  resendCard: boolean;
}

export const CardInfoContent: React.FC<ICardInfoContentProps> = memo(({ resendCard }) => {
  const card = useCardContext((state) => state.card);
  const musicList = useCardContext((state) => state.musicList);
  const setShowKeyboard = useCardContext((state) => state.setShowKeyboard);
  const setActiveTextFieldId = useCardContext((state) => state.setActiveTextFieldId);

  const [editAllFields, setEditAllFields] = useState(false);

  const resendHasAudio = resendCard && typeof card?.orderAudioId === 'number';
  const canShowAudioBlock =
    musicList && !isEmptyObject(musicList) && (resendHasAudio || !resendCard);

  const musicPrice = card?.music_price || 5;
  let price = card?.cardPrice || 0;

  if (card?.audio_id) {
    price += musicPrice;
  }

  const canShowSecondaryImage = Boolean(card?.secondaryImage?.is_enabled);
  const canShowLogo = Boolean(card?.logo?.is_enabled);

  const { categoryTitle, thereAreHiddenFields } = useMemo(() => {
    const result = {
      categoryTitle: '',
      thereAreHiddenFields: false,
    };

    if (!card) return result;

    result.categoryTitle = card.categories.reduce((acc, item) => {
      if (acc.length > 0) {
        return acc + ' + ' + item.name;
      }

      return acc + item.name;
    }, '');

    result.thereAreHiddenFields = card.fields.some((field) => field.isHidden);

    return result;
  }, [card]);

  const canShowTopButtons = canShowLogo || canShowSecondaryImage || thereAreHiddenFields;

  const onClickEditAllFields = useCallback(() => {
    setEditAllFields((prev) => !prev);
    setShowKeyboard(false);
    setActiveTextFieldId(null);
  }, []);

  return (
    <>
      <div className={styles.cardInfoContainer}>
        <div className={styles.headContainer}>
          <HtsTypography element="h1" variant="h2">
            {card?.name && `#${card.name}`}
          </HtsTypography>

          <HtsTypography variant="subtitle">{categoryTitle.toUpperCase()}</HtsTypography>
        </div>

        {canShowTopButtons && (
          <div className={styles.topButtonsContainer}>
            <TopButtons
              thereAreHiddenFields={thereAreHiddenFields}
              canShowLogo={canShowLogo}
              canShowSecondaryImage={canShowSecondaryImage}
              editAllFields={editAllFields}
              onClickEditAllFields={onClickEditAllFields}
            />
          </div>
        )}

        <div className={styles.fieldsContainer}>
          <TextFields editAllFields={editAllFields} />
        </div>
      </div>

      <div className={styles.buyContainer}>
        {(canShowAudioBlock || !resendCard) && (
          <div className={styles.musicTotalContainer}>
            {canShowAudioBlock && <AddMusic resendCard={resendCard} musicPrice={musicPrice} />}

            {!resendCard && (
              <div className={styles.totalContainer}>
                <HtsTypography variant="body2">Total:</HtsTypography>

                <HtsTypography element="span" variant="h1">
                  &#36;
                  {price}
                </HtsTypography>
              </div>
            )}
          </div>
        )}

        <div className={styles.bottomButtonsContainer}>
          {!resendCard ? <BuyButtons price={price} /> : <ResendButton />}
        </div>
      </div>
    </>
  );
});
